import { Box, Typography, IconButton, ShareButton, SettingsIcon, ArrowBackIcon, PublicOutlinedIcon,
  PublicOffOutlinedIcon, SVGIcon, TripTrackingTabs, Slide, RetryImage
} from 'src/components';
import { useEffect, useState } from 'react';
import { Loader } from 'src/components';
import { getTripSteps, tripTrackingShareUrl, useAuth } from 'src/utils';
import { TripDetailsSteps, Statistics, VisualisationsMenu } from '../../index';
import { getTransportMode } from 'src/data';
import { useIsMobile } from 'src/hooks';
import { format } from 'date-fns';

import placeholderImage from 'src/assets/placeholders/600x400.png';

const HEADER_IMAHE_HEIGHT = 300;

export default function TripDetails({ 
  isPublicPortal,
  trip, 
  steps, 
  setSteps, 
  handleViewChange, 
  handleHttpFailure, 
  transportMode, 
  setDrawerState, 
  totalTripKms, 
  totalTripTime,
  toggleVisualisationView,
  setSelectedStep,
  mapRef}) {

  const [loadingSteps, setLoadingSteps] = useState(trip.stepCount > 0);
  const [slideDirection, setSlideDirection] = useState("left");
  const [tab, setTab] = useState(0);

  const isMobile = useIsMobile();
  const { user } = useAuth();

  const totalDistance = Math.round(totalTripKms).toLocaleString('en-US');

  const showPolarstepsUrl = trip?.polarstepsUrl !== undefined && trip?.polarstepsUrl !== null;

  const handleTabChange = (event, newValue) => {
    if (newValue > tab) {
      setSlideDirection("left"); // Going forward
    } else {
      setSlideDirection("right"); // Going backward
    }
    setTab(newValue);
  };

  const handleEditTripClicked = () => {
    handleViewChange('EditTrip', trip);
    setDrawerState("expanded");
  };

  useEffect(() => {
    const fetchSteps = async () => {
      try {
        if (trip.stepCount > 0) {
          const res = await getTripSteps(trip.id);
          setSteps(res); 
        }
      } catch (err) {
        console.error('Failed to load steps:', err);
        handleHttpFailure('Failed to load trip steps.');
      } finally {
        setLoadingSteps(false);
      }
    };

    if (!isPublicPortal) {
      fetchSteps();
    }
  }, [trip.id, trip.stepCount, isPublicPortal]);

  return (
    <Box className="scrollBox">
    <Box sx={{backgroundColor: '#f2f5f7', pb: 5, minHeight: `calc(100dvh - 120px)`}}>
      {/* Header Image with overlay */}
      <RetryImage
        src={trip.coverImageUrl}
        placeholder={placeholderImage}
        mode="bg"
        style={{
          position: 'relative',
          top: 0,
          left: 0,
          width: '100%',
          height: HEADER_IMAHE_HEIGHT,
          borderRadius: 1,
          overflow: 'hidden',
          mb: 1,
          zIndex: 0
        }}
      >
        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.3)',
          }}
        />

        {/* Top Bar */}
        {!isMobile && (
          <Box
            sx={{
              position: 'relative',
              top: 0,
              left: 0,
              width: '100%',
              px: 2,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              zIndex: 1,
            }}
          >
            <IconButton
              size="small"
              onClick={() => handleViewChange('Overview')}
              sx={{ color: 'white' }}
              disableRipple
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
              {trip.name}
            </Typography>
            <Box width={40} /> {/* spacer to balance back icon */}
          </Box>
        )}

        {/* Bottom Info Section */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            px: 2,
            pb: 2,
            zIndex: 1,
            color: 'white',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, mr: 1 }}>
              {trip.description ? trip.description : 'No description added yet'}
            </Typography>
            <SVGIcon
              icon={getTransportMode(transportMode).icon}
              size={80}
              color="#fff"
            />
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
          >
            <Box display="flex" gap={2} alignItems="center">
              <Typography variant="body2">
                {format(new Date(trip.startDate), 'MMM dd, yyyy')}
              </Typography>
              <Typography variant="body2">{totalDistance} km</Typography>
              <Typography variant="body2">
                {isPublicPortal ? trip.steps?.length : trip.stepCount} steps
              </Typography>
            </Box>

            {!isPublicPortal && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {trip.privacy === 'Public' ? (
                  <PublicOutlinedIcon fontSize="small" />
                ) : (
                  <PublicOffOutlinedIcon fontSize="small" />
                )}
                <ShareButton
                  url={tripTrackingShareUrl(user?.id, trip?.id)}
                  size="small"
                  sx={{ color: '#fff', ml: 1 }}
                />
                <IconButton
                  onClick={() => handleEditTripClicked()}
                  sx={{ color: 'white' }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </RetryImage>

      {!isPublicPortal && (
        <TripTrackingTabs 
          tab={tab}
          handleTabChange={handleTabChange}
          label="Steps" />
      )}

      <Box sx={{ mx: 2}}>
        {loadingSteps ? (
          <Loader />
        ) : (<>
          <Slide direction={slideDirection} in={tab === 0} mountOnEnter unmountOnExit timeout={500}>
              <Box sx={{ flex: 1, display: tab === 0 ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}>
                  <TripDetailsSteps
                    isPublicPortal={isPublicPortal}
                    steps={steps}
                    tripStartDate={trip.startDate}
                    handleViewChange={handleViewChange}
                    setSelectedStep={setSelectedStep}
                    setDrawerState={setDrawerState}
                    showPolarstepsUrl={showPolarstepsUrl}
                  />
              </Box>
          </Slide>  

          <Slide direction={slideDirection} in={tab === 1} mountOnEnter unmountOnExit timeout={500}>
              <Box sx={{ flex: 1, display: tab === 1 ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}>
                  <Statistics 
                    trips={[trip]} 
                    type="trip" 
                    title={`${trip.name} Trip Summary`}
                    steps={steps}
                    totalTripKms={totalTripKms}
                    totalTripTime={totalTripTime} />
              </Box>
          </Slide> 

          <Slide direction={slideDirection} in={tab === 2} mountOnEnter unmountOnExit timeout={500}>
              <Box sx={{ flex: 1, display: tab === 2 ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}>
                  <VisualisationsMenu 
                    setDrawerState={setDrawerState}
                    toggleVisualisationView={toggleVisualisationView}
                    mapRef={mapRef} />
              </Box>
          </Slide>     
        </>)}
      </Box>
    </Box>
    </Box>
  );
}
